// src/components/common/SidebarNavItem.tsx
import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

interface SidebarNavItemProps {
  text: string;
  icon: SvgIconComponent;
  path: string;
  color: string;
  active: boolean;
  collapsed: boolean;
  isRTL: boolean;
  onNavigate: (path: string) => void;
  badgeCount?: number;
  tourTarget?: string;
}

const tooltipSx = {
  bgcolor: 'white',
  color: '#1e293b',
  fontSize: '0.813rem',
  fontFamily: 'Cairo, Arabic, sans-serif',
  border: '1px solid rgba(209, 213, 219, 0.3)',
};

const tooltipArrowSx = {
  color: 'white',
  '&::before': {
    border: '1px solid rgba(209, 213, 219, 0.3)',
  },
};

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  text,
  icon: Icon,
  path,
  color,
  active,
  collapsed,
  isRTL,
  onNavigate,
  badgeCount,
  tourTarget,
}) => {
  return (
    <ListItem disablePadding sx={{ mb: 0.5, }}>
      <Tooltip
        title={collapsed ? text : ''}
        placement={isRTL ? 'left' : 'right'}
        arrow
        componentsProps={{
          tooltip: { sx: tooltipSx },
          arrow: { sx: tooltipArrowSx },
        }}
      >
        <ListItemButton
          data-tour={tourTarget}
          selected={active}
          onClick={() => onNavigate(path)}
          sx={{
            borderRadius: 2.5,
            px: collapsed ? 1.5 : 2,
            py: 1.5,
            mb: 0.5,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: collapsed ? 'center' : 'flex-start',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              bgcolor: active ? `${color}15` : `${color}10`,
              transform: isRTL ? 'translateX(-2px)' : 'translateX(2px)',
              ...(!active && {
                '& .MuiListItemIcon-root': {
                  color: color,
                },
              }),
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 'auto',
              marginInlineEnd: collapsed ? 0 : 2,
              color: active ? color : 'rgb(100, 116, 139)',
              transition: 'color 0.2s ease',
            }}
          >
            <Badge 
              badgeContent={badgeCount} 
              color="error" 
              sx={{ 
                '& .MuiBadge-badge': { 
                  fontFamily: 'Cairo, Arabic, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '0.65rem',
                  height: 18,
                  minWidth: 18,
                }
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Badge>
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={text}
              sx={{
                m: 0,
                textAlign: 'right',
                '& .MuiListItemText-primary': {
                  fontFamily: 'Cairo, Arabic, sans-serif',
                  fontSize: '0.938rem',
                  fontWeight: active ? 700 : 600,
                  color: active ? color : 'rgb(71, 85, 105)',
                  transition: 'color 0.2s ease',
                },
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default SidebarNavItem;
